import { useState, useRef, useEffect } from "react";
import { Trash2, ChevronDown, Check, Repeat, CalendarRange, CalendarDays, GripVertical } from "lucide-react";
import { useScheduleEditor, useSelectedSchedule } from "../ScheduleEditorProvider";
import type { Schedule, DayOfWeek, ScheduleType, Slideshow } from "../types";
import { DAYS, COLORS } from "../constants";
import { formatTime, getDateForDay, stripUtcSuffix, isUtcDatetime } from "../utils";
import { HierarchyPicker } from "../../../components/HierarchyPicker/HierarchyPicker";
import { useTenant } from "../../../providers/TenantProvider";

// --- Field Components ---

type FieldProps = {
  label: string;
  children: React.ReactNode;
};

const Field = ({ label, children }: FieldProps) => (
  <div>
    <label className="text-sm mb-1 block" style={{ color: COLORS.white70 }}>
      {label}
    </label>
    {children}
  </div>
);

// --- Day Selector ---

type DaySelectorProps = {
  selected: DayOfWeek[];
  onChange: (days: DayOfWeek[]) => void;
  disabled?: boolean;
};

const DaySelector = ({ selected, onChange, disabled }: DaySelectorProps) => {
  const toggleDay = (day: DayOfWeek) => {
    if (disabled) return;
    const has = selected.includes(day);
    const newDays = has ? selected.filter((d) => d !== day) : [...selected, day];
    if (newDays.length > 0) onChange(newDays);
  };

  return (
    <div className="flex gap-1">
      {DAYS.map((d) => (
        <button
          key={d.key}
          onClick={() => toggleDay(d.key)}
          disabled={disabled}
          className="flex-1 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: selected.includes(d.key) ? COLORS.orange : COLORS.white10,
            color: selected.includes(d.key) ? COLORS.white : COLORS.white50,
          }}
        >
          {d.letter}
        </button>
      ))}
    </div>
  );
};

// --- Schedule Type Selector ---

const TYPE_OPTIONS: { value: ScheduleType; label: string; icon: typeof Repeat }[] = [
  { value: "recurring", label: "Recurring (weekly)", icon: Repeat },
  { value: "date-range", label: "Date range", icon: CalendarRange },
  { value: "one-time", label: "One-time event", icon: CalendarDays },
];

type TypeSelectorProps = {
  schedule: Schedule;
  onUpdate: (updates: Partial<Schedule>) => void;
  weekStart: Date;
  disabled?: boolean;
};

const TypeSelector = ({ schedule, onUpdate, weekStart, disabled }: TypeSelectorProps) => {
  const useUtc = isUtcDatetime(schedule.startDate || "");

  // Format datetime value for the input (strip Z and seconds)
  const inputValue = (val?: string) => (val ? stripUtcSuffix(val) : "");

  // Apply UTC suffix if needed when saving
  const saveDatetime = (val: string) => (useUtc ? `${val}:00Z` : val);

  const toggleUtc = () => {
    const updates: Partial<Schedule> = {};
    if (useUtc) {
      // Switch to local — strip Z from stored values
      if (schedule.startDate) updates.startDate = stripUtcSuffix(schedule.startDate);
      if (schedule.endDate) updates.endDate = stripUtcSuffix(schedule.endDate);
    } else {
      // Switch to UTC — append :00Z to stored values
      if (schedule.startDate) updates.startDate = `${stripUtcSuffix(schedule.startDate)}:00Z`;
      if (schedule.endDate) updates.endDate = `${stripUtcSuffix(schedule.endDate)}:00Z`;
    }
    onUpdate(updates);
  };

  const inputClassName = "w-full rounded px-3 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed";
  const inputStyle = { backgroundColor: COLORS.white10, border: `1px solid ${COLORS.white20}` };

  return (
    <div>
      <div className="space-y-1">
        {TYPE_OPTIONS.map(({ value, label, icon: Icon }) => {
          const isActive = schedule.type === value;
          return (
            <button
              key={value}
              onClick={() => {
                if (disabled) return;
                const updates: Partial<Schedule> = { type: value };
                if ((value === "date-range" || value === "one-time") && !schedule.startDate) {
                  const dayKey = schedule.daysOfWeek[0];
                  const dayIndex = DAYS.findIndex((d) => d.key === dayKey);
                  if (dayIndex >= 0) {
                    const dateStr = getDateForDay(weekStart, dayIndex);
                    if (value === "date-range") {
                      updates.startDate = `${dateStr}T00:00`;
                      updates.endDate = `${dateStr}T23:59`;
                    } else {
                      // one-time just uses a plain date
                      updates.startDate = dateStr;
                    }
                  }
                }
                onUpdate(updates);
              }}
              disabled={disabled}
              className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isActive ? `${COLORS.orange}33` : COLORS.white10,
                color: isActive ? COLORS.orange : COLORS.white70,
                border: isActive ? `1px solid ${COLORS.orange}` : "1px solid transparent",
              }}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </div>

      {schedule.type === "date-range" && (
        <div className="mt-2 space-y-2">
          <div>
            <label className="text-xs mb-1 block" style={{ color: COLORS.white50 }}>
              Active From
            </label>
            <input
              type="datetime-local"
              value={inputValue(schedule.startDate)}
              onChange={(e) => onUpdate({ startDate: saveDatetime(e.target.value) })}
              disabled={disabled}
              className={inputClassName}
              style={inputStyle}
            />
          </div>
          <div>
            <label className="text-xs mb-1 block" style={{ color: COLORS.white50 }}>
              Active Until
            </label>
            <input
              type="datetime-local"
              value={inputValue(schedule.endDate)}
              onChange={(e) => onUpdate({ endDate: saveDatetime(e.target.value) })}
              disabled={disabled}
              className={inputClassName}
              style={inputStyle}
            />
          </div>

          {/* UTC / Local toggle */}
          <button
            type="button"
            onClick={() => !disabled && toggleUtc()}
            disabled={disabled}
            className="flex items-center gap-2 text-xs py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: COLORS.white50 }}
          >
            <div
              className="w-7 h-4 rounded-full relative transition-colors"
              style={{ backgroundColor: useUtc ? COLORS.orange : COLORS.white20 }}
            >
              <div
                className="w-3 h-3 rounded-full bg-white absolute top-0.5 transition-all"
                style={{ left: useUtc ? 14 : 2 }}
              />
            </div>
            {useUtc ? "UTC (universal time)" : "Local time (per device)"}
          </button>
        </div>
      )}

      {schedule.type === "one-time" && (
        <div className="mt-2">
          <label className="text-xs mb-1 block" style={{ color: COLORS.white50 }}>
            Event Date
          </label>
          <input
            type="date"
            value={schedule.startDate || ""}
            onChange={(e) => onUpdate({ startDate: e.target.value })}
            disabled={disabled}
            className={inputClassName}
            style={inputStyle}
          />
        </div>
      )}
    </div>
  );
};

// --- Delete Confirmation ---

type DeleteSectionProps = {
  onDelete: () => void;
  disabled?: boolean;
};

const DeleteSection = ({ onDelete, disabled }: DeleteSectionProps) => {
  const [showConfirm, setShowConfirm] = useState(false);

  if (showConfirm) {
    return (
      <div className="flex gap-2">
        <button
          onClick={() => setShowConfirm(false)}
          className="flex-1 py-2 rounded text-sm text-white"
          style={{ backgroundColor: COLORS.white10 }}
        >
          Cancel
        </button>
        <button onClick={onDelete} className="flex-1 py-2 bg-red-600 text-white rounded text-sm">
          Delete
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setShowConfirm(true)}
      disabled={disabled}
      className="w-full py-2 bg-red-600/20 text-red-400 rounded text-sm flex items-center justify-center gap-2 hover:bg-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Trash2 className="w-4 h-4" /> Delete Schedule
    </button>
  );
};

// --- Slideshow Item (draggable) ---

type SlideshowItemProps = {
  slideshow: Slideshow;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
};

const SlideshowItem = ({ slideshow, isDragging, onDragStart, onDragEnd }: SlideshowItemProps) => (
  <div
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData("slideshowId", slideshow.id);
      onDragStart();
    }}
    onDragEnd={onDragEnd}
    className="flex items-center gap-2 p-2 rounded cursor-grab active:cursor-grabbing transition-all"
    style={{
      opacity: isDragging ? 0.5 : 1,
      backgroundColor: "transparent",
    }}
    onMouseEnter={(e) => {
      if (!isDragging) e.currentTarget.style.backgroundColor = COLORS.white10;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = "transparent";
    }}
  >
    <GripVertical className="w-4 h-4" style={{ color: COLORS.white20 }} />
    <div className="w-3 h-3 rounded" style={{ backgroundColor: slideshow.color }} />
    <span className="text-white text-sm truncate">{slideshow.name}</span>
  </div>
);

// --- Slideshow List (when no schedule selected) ---

const SlideshowList = () => {
  const { slideshows } = useScheduleEditor();
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <div className="flex-1 flex flex-col">
      <div className="p-4" style={{ borderBottom: `1px solid ${COLORS.white10}` }}>
        <p className="text-xs" style={{ color: COLORS.white50 }}>
          Drag a slideshow onto the calendar to create a new schedule
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {slideshows.map((slideshow) => (
          <SlideshowItem
            key={slideshow.id}
            slideshow={slideshow}
            isDragging={draggingId === slideshow.id}
            onDragStart={() => setDraggingId(slideshow.id)}
            onDragEnd={() => setDraggingId(null)}
          />
        ))}
      </div>
    </div>
  );
};

// --- Slideshow Picker ---

type SlideshowPickerProps = {
  slideshows: Slideshow[];
  selectedId: string;
  onChange: (id: string) => void;
  disabled?: boolean;
};

const SlideshowPicker = ({ slideshows, selectedId, onChange, disabled }: SlideshowPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = slideshows.find((s) => s.id === selectedId);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-zinc-600"
        } ${isOpen ? "border-blue-500" : ""}`}
        style={{ backgroundColor: "rgb(39 39 42)", border: `1px solid rgb(63 63 70)` }}
      >
        {selected && <div className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: selected.color }} />}
        <span className="flex-1 truncate text-white">{selected?.name || "Select slideshow..."}</span>
        <ChevronDown size={16} className={`text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-64 overflow-auto">
          <div className="p-1">
            {slideshows.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  onChange(s.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded transition-colors ${
                  s.id === selectedId ? "bg-zinc-700 text-white" : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                }`}
              >
                <div className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: s.color }} />
                <span className="flex-1 truncate">{s.name}</span>
                {s.id === selectedId && <Check size={14} className="text-blue-400" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Panel ---

export default () => {
  const {
    updateSchedule,
    deleteSchedule,
    slideshows,
    groups,
    canCrossTenantBroadcast,
    getSlideshow,
    canEditSchedule,
    weekStart,
  } = useScheduleEditor();
  const { currentTenantId } = useTenant();
  const schedule = useSelectedSchedule();

  const slideshow = schedule ? getSlideshow(schedule.slideshowId) : undefined;
  const isEditable = schedule ? canEditSchedule(schedule) : true;

  const handleUpdate = (updates: Partial<Schedule>) => {
    if (schedule && isEditable) updateSchedule(schedule.id, updates);
  };

  const handleDelete = () => {
    if (schedule && isEditable) deleteSchedule(schedule.id);
  };

  const handleTargetGroupsChange = (ids: string[]) => {
    if (!schedule || !isEditable || ids.length === 0) return;
    handleUpdate({
      targetGroupIds: ids,
      groupId: ids.length === 1 ? ids[0]! : (currentTenantId ?? schedule.groupId),
    });
  };

  const handleTargetGroupChange = (id: string | null) => {
    if (!schedule || !isEditable || !id) return;
    handleUpdate({ groupId: id, targetGroupIds: [id] });
  };

  return (
    <div
      className="w-72 flex flex-col"
      style={{ backgroundColor: COLORS.navy, borderLeft: `1px solid ${COLORS.white10}` }}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${COLORS.white10}` }}>
        <h2 style={{ color: COLORS.orange }} className="font-bold">
          {schedule ? "Edit Schedule" : "Slideshows"}
        </h2>
      </div>

      {/* Read-only warning - show when user cannot edit */}
      {schedule && !isEditable && (
        <div
          className="mx-4 mt-4 p-3 rounded text-sm"
          style={{
            backgroundColor: `${COLORS.orange}15`,
            color: COLORS.orange,
            border: `1px solid ${COLORS.orange}33`,
          }}
        >
          This schedule belongs to a higher-level group and cannot be edited from here.
        </div>
      )}

      {/* Content - slideshow list or edit form */}
      {!schedule ? (
        <SlideshowList />
      ) : (
        <>
          {/* Form content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Name */}
            <Field label="Name">
              <input
                type="text"
                value={schedule.name}
                onChange={(e) => handleUpdate({ name: e.target.value })}
                disabled={!isEditable}
                className="w-full rounded px-3 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: COLORS.white10, border: `1px solid ${COLORS.white20}` }}
              />
            </Field>

            {/* Slideshow */}
            <Field label="Slideshow">
              <SlideshowPicker
                slideshows={slideshows}
                selectedId={schedule.slideshowId}
                onChange={(id) => handleUpdate({ slideshowId: id })}
                disabled={!isEditable}
              />
              <div className="h-1.5 rounded mt-2" style={{ backgroundColor: slideshow?.color }} />
            </Field>

            {/* Target Group */}
            <Field label="Applies To">
              {canCrossTenantBroadcast ? (
                <HierarchyPicker
                  groups={groups}
                  allowMultiple
                  selectedIds={schedule.targetGroupIds}
                  onChange={handleTargetGroupsChange}
                  placeholder="Select target..."
                  disabled={!isEditable}
                />
              ) : (
                <HierarchyPicker
                  groups={groups}
                  selectedId={schedule.groupId}
                  onChange={handleTargetGroupChange}
                  placeholder="Select target..."
                  disabled={!isEditable}
                />
              )}
              <p className="text-xs mt-1" style={{ color: COLORS.white50 }}>
                {canCrossTenantBroadcast
                  ? "Choose one or more groups this schedule applies to"
                  : "Choose which part of the organization this schedule applies to"}
              </p>
            </Field>

            {/* Exclusive Mode */}
            <Field label="Exclusive Mode">
              <button
                type="button"
                onClick={() => isEditable && handleUpdate({ exclusive: !schedule.exclusive })}
                disabled={!isEditable}
                className="flex items-center gap-2 text-sm py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: schedule.exclusive ? COLORS.orange : COLORS.white50 }}
              >
                <div
                  className="w-8 h-[18px] rounded-full relative transition-colors shrink-0"
                  style={{ backgroundColor: schedule.exclusive ? COLORS.orange : COLORS.white20 }}
                >
                  <div
                    className="w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 transition-all"
                    style={{ left: schedule.exclusive ? 15 : 2 }}
                  />
                </div>
                {schedule.exclusive ? "Overrides lower-priority schedules" : "Merges with other schedules"}
              </button>
              <p className="text-xs mt-1" style={{ color: COLORS.white50 }}>
                When enabled, this schedule will replace lower-priority schedules during its active time
              </p>
            </Field>

            {/* Daily Hours */}
            <Field label="Daily Hours">
              <div className="flex gap-2 items-center">
                <input
                  type="time"
                  value={schedule.startTime}
                  onChange={(e) => handleUpdate({ startTime: e.target.value })}
                  disabled={!isEditable}
                  className="flex-1 rounded px-2 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: COLORS.white10, border: `1px solid ${COLORS.white20}` }}
                />
                <span style={{ color: COLORS.white50 }}>to</span>
                <input
                  type="time"
                  value={schedule.endTime}
                  onChange={(e) => handleUpdate({ endTime: e.target.value })}
                  disabled={!isEditable}
                  className="flex-1 rounded px-2 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: COLORS.white10, border: `1px solid ${COLORS.white20}` }}
                />
              </div>
              <p className="text-xs mt-1" style={{ color: COLORS.white50 }}>
                {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
              </p>
            </Field>

            {/* Schedule Type */}
            <Field label="Schedule Type">
              <TypeSelector schedule={schedule} onUpdate={handleUpdate} weekStart={weekStart} disabled={!isEditable} />
            </Field>

            {/* Days - hidden for one-time events */}
            {schedule.type !== "one-time" && (
              <Field label="Active Days">
                <DaySelector
                  selected={schedule.daysOfWeek}
                  onChange={(days) => handleUpdate({ daysOfWeek: days })}
                  disabled={!isEditable}
                />
              </Field>
            )}
          </div>

          {/* Actions section */}
          <div className="p-4 space-y-3" style={{ borderTop: `1px solid ${COLORS.white10}` }}>
            <DeleteSection onDelete={handleDelete} disabled={!isEditable} />
          </div>
        </>
      )}
    </div>
  );
};
